import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSavingsVaults } from "@/features/savings/api/use-savings-vaults";
import type { SavingsVaultFilter } from "@/features/savings/api/dto";
import {
  SavingsSummaryCards,
  SavingsVaultFormDialog,
  savingsVaultsColumns,
  type SavingsVaultRow,
} from "@/features/savings/components";

/** Traduit les filtres de colonnes DataTableClar en `SavingsVaultFilter` (Postman `POST /SavingsVaults/all`). */
const toSavingsVaultFilter = (
  filters: ColumnFiltersState
): SavingsVaultFilter => {
  const filter: SavingsVaultFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "status") filter.status = value as SavingsVaultFilter["status"];
    if (id === "ownerKeycloakUserId") filter.ownerKeycloakUserId = value;
  }
  return filter;
};

/**
 * Liste paginée des coffres d'épargne (`POST /SavingsVaults/all`, guide
 * v3.1 §6.10) — cartes d'agrégat `summary` en tête, filtres `status`
 * (select) et `ownerKeycloakUserId` (texte, back-office). Route cible :
 * `/app/wallet/savings-vaults` (câblage router/nav : phase 18.7).
 */
export function SavingsVaultsPage() {
  const { t } = useTypedTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toSavingsVaultFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useSavingsVaults(params);

  const rows = useMemo(
    () => (data?.items ?? []).filter((v): v is SavingsVaultRow => Boolean(v.id)),
    [data]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("savings.vaults.title")}
        </h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("savings.vaults.create")}
        </Button>
      </div>

      <SavingsSummaryCards />

      <DataTableClar
        data={rows}
        columnsClar={savingsVaultsColumns}
        title={t("savings.vaults.title")}
        exportFileName="savings-vaults"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <SavingsVaultFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
